import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { decodeShareState, formatMoney, type SharedSplit } from '@bill/core';
import { Shell } from '../components/Shell.jsx';

/**
 * `/s#…` — what a recipient sees when they open a share link.
 *
 * The state lives after the `#`, which browsers never send to the server, so
 * this page is static and the split it shows was never stored anywhere.
 */
export function Shared(): React.ReactElement {
  const [state, setState] = useState<{ split: SharedSplit } | { error: string } | null>(null);

  useEffect(() => {
    decodeShareState(window.location.hash)
      .then((split) => setState({ split }))
      .catch((error: unknown) => setState({ error: error instanceof Error ? error.message : String(error) }));
  }, []);

  if (!state) return <Shell title="Opening…">{null}</Shell>;

  if ('error' in state) {
    return (
      <Shell title="This link doesn't work">
        <p className="hint">{state.error}</p>
        <p className="hint">
          <Link to="/">Split a bill of your own</Link>.
        </p>
      </Shell>
    );
  }

  const { split } = state;
  return (
    <Shell
      title={split.merchant ?? 'Bill split'}
      lede={`${formatMoney(split.grandTotal, split.currency)} total`}
      actions={
        <Link to="/" className="button primary big">
          Split your own bill
        </Link>
      }
    >
      <ul className="cards">
        {split.lines.map((line, i) => (
          <li key={i} className="card">
            <div className="card-head">
              <span className="card-name">{line.name}</span>
              <span className="card-total">{formatMoney(line.total, split.currency)}</span>
            </div>
            {line.items.length > 0 && <p className="card-items">{line.items.join(', ')}</p>}
          </li>
        ))}
      </ul>
    </Shell>
  );
}
